/**
 * MagazineOverview.js
 * Admin overview dashboard showing metrics (Active, Total, Expiring) and trends.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import {
  Users, RefreshCw, BarChart2, TrendingUp, Calendar, Inbox, AlertCircle, BookOpen
} from 'lucide-react';
import DateRangePicker from '../../../components/DateRangePicker';

const MagazineOverview = ({ toast }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    active_subscribers: 0,
    total_subscribers: 0,
    expiring_30_days: 0,
    total_copy_orders: 0,
    chart_data: []
  });
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMagazineOverview(filters);
      if (res.success && res.data) {
        setStats(res.data);
      } else {
        toast.error(res.error || 'Failed to fetch overview data.');
      }
    } catch {
      toast.error('Failed to load overview data.');
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Find max value in chart to scale SVG bar heights
  const chartData = stats?.chart_data || [];
  const maxVal = Math.max(
    ...chartData.map(d => Math.max(d.subscriptions || 0, d.orders || 0)),
    5
  );

  return (
    <div className="space-y-6">
      {/* Filters and Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 pb-5">
        <div className="text-left">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <BarChart2 size={18} className="text-[#2ab391]" />
            Magazine Analytics Overview
          </h3>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Real-time counts, expiration warnings, and subscription trends.</p>
        </div>

        {/* Date Filters */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-100 p-2.5 rounded-xl text-left self-start sm:self-center">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date:</span>
            <DateRangePicker
              startDate={filters.start_date}
              endDate={filters.end_date}
              onChange={(start, end) => {
                setFilters(prev => ({ ...prev, start_date: start, end_date: end }));
              }}
              placeholder="Select Date Range"
            />
          </div>
          {(filters.start_date || filters.end_date) && (
            <button
              onClick={() => setFilters({ start_date: '', end_date: '' })}
              className="px-2.5 py-1 text-xs font-semibold text-red-650 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
          <span className="text-sm font-semibold text-gray-500">Calculating Analytics...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Analytics KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Active Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-xs flex items-center gap-4 text-left">
              <div className="p-3.5 bg-emerald-50 rounded-xl text-emerald-500">
                <Users size={20} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Active Subscribers</p>
                <h4 className="text-2xl font-bold text-gray-800 mt-0.5">{stats.active_subscribers}</h4>
                <p className="text-[9px] text-emerald-600 font-medium mt-0.5">Currently receiving print editions</p>
              </div>
            </div>

            {/* Total Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-xs flex items-center gap-4 text-left">
              <div className="p-3.5 bg-teal-50 rounded-xl text-[#2ab391]">
                <TrendingUp size={20} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Orders Completed</p>
                <h4 className="text-2xl font-bold text-gray-800 mt-0.5">{stats.total_subscribers}</h4>
                <p className="text-[9px] text-[#2ab391] font-medium mt-0.5">Cumulative lifetime subscriptions</p>
              </div>
            </div>

            {/* Copy Orders Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-xs flex items-center gap-4 text-left">
              <div className="p-3.5 bg-indigo-50 rounded-xl text-indigo-500">
                <BookOpen size={20} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Copy Sales</p>
                <h4 className="text-2xl font-bold text-gray-800 mt-0.5">{stats.total_copy_orders}</h4>
                <p className="text-[9px] text-indigo-650 font-medium mt-0.5">Single print edition copy purchases</p>
              </div>
            </div>

            {/* Expiring Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-xs flex items-center gap-4 text-left">
              <div className="p-3.5 bg-amber-50 rounded-xl text-amber-500">
                <AlertCircle size={20} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Expiring in 30 Days</p>
                <h4 className="text-2xl font-bold text-gray-800 mt-0.5">{stats.expiring_30_days}</h4>
                <p className="text-[9px] text-amber-600 font-medium mt-0.5">Requires renewal follow-up</p>
              </div>
            </div>
          </div>

          {/* SVG Trend Graph */}
          <div className="bg-white rounded-2xl border border-gray-150 p-5 shadow-xs text-left">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-[#2ab391]" />
              Subscription & Copy Sales Trend
            </h4>

            {chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2 border border-dashed border-gray-150 rounded-xl">
                <Inbox size={28} className="text-gray-300" />
                <p className="text-xs text-gray-400 font-bold">No data matches this date range.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* SVG Graph wrapper */}
                <div className="w-full overflow-x-auto pb-2">
                  <div className="min-w-[500px] h-[220px] relative">
                    <svg viewBox="0 0 500 180" className="w-full h-full overflow-visible">
                      {/* Gridlines */}
                      <line x1="40" y1="20" x2="480" y2="20" stroke="#f1f5f9" strokeWidth="1" />
                      <line x1="40" y1="65" x2="480" y2="65" stroke="#f1f5f9" strokeWidth="1" />
                      <line x1="40" y1="110" x2="480" y2="110" stroke="#f1f5f9" strokeWidth="1" />
                      <line x1="40" y1="150" x2="480" y2="150" stroke="#cbd5e1" strokeWidth="1" />

                      {/* Y-Axis labels */}
                      <text x="30" y="24" fill="#94a3b8" fontSize="9" textAnchor="end">{Math.round(maxVal)}</text>
                      <text x="30" y="69" fill="#94a3b8" fontSize="9" textAnchor="end">{Math.round(maxVal / 2)}</text>
                      <text x="30" y="114" fill="#94a3b8" fontSize="9" textAnchor="end">{Math.round(maxVal / 4)}</text>
                      <text x="30" y="154" fill="#94a3b8" fontSize="9" textAnchor="end">0</text>

                      {/* Draw Bars */}
                      {chartData.map((item, index) => {
                        const count = chartData.length;
                        const x = 40 + (index * (440 / count)) + ((440 / count) - 24) / 2;
                        
                        // Subscriptions bar
                        const subH = (item.subscriptions / maxVal) * 130;
                        const subY = 150 - subH;
                        
                        // Copy orders bar
                        const ordH = (item.orders / maxVal) * 130;
                        const ordY = 150 - ordH;

                        const dateFormatted = new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

                        return (
                          <g key={item.date} className="group/bar">
                            {/* Subscriptions */}
                            <rect
                              x={x}
                              y={subY}
                              width="10"
                              height={subH}
                              fill="#2ab391"
                              rx="2"
                              className="transition-all duration-200 hover:fill-[#208c71]"
                            />
                            {/* Copy Orders */}
                            <rect
                              x={x + 12}
                              y={ordY}
                              width="10"
                              height={ordH}
                              fill="#6366f1"
                              rx="2"
                              className="transition-all duration-200 hover:fill-[#4f46e5]"
                            />

                            {/* Label */}
                            <text
                              x={x + 11}
                              y="168"
                              fill="#64748b"
                              fontSize="8"
                              fontWeight="600"
                              textAnchor="middle"
                            >
                              {dateFormatted}
                            </text>

                            {/* Tooltip on hover */}
                            <title>
                              Date: {item.date}
                              &#10;Yearly Subscriptions: {item.subscriptions}
                              &#10;Copy Orders: {item.orders}
                            </title>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-5 justify-center text-xs font-semibold text-gray-500 pt-2 border-t border-gray-50">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-[#2ab391] block" />
                    <span>Yearly Subscriptions</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-indigo-500 block" />
                    <span>Print copy sales</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MagazineOverview;
